
import { defineComponent, nextTick, onMounted, ref } from 'vue'
import { HistoryQueryStringType } from '@/enums/queryString'
import { useHistory } from '@/hooks/useHistory'
import Hammer from 'hammerjs'

/**
 * 패널을 이동합니다.
 */
function movePanel(
  element: HTMLElement,
  startPosition: number,
  targetPosition: number,
  step: number,
  done?: () => void,
  progress?: (pos: number) => void
) {
  let current = startPosition
  step = startPosition < targetPosition ? step : -step

  const repeater = setInterval(() => {
    if (step < 0) {
      current = Math.max(current + step, targetPosition)
    } else {
      current = Math.min(current + step, targetPosition)
    }

    element.style.transform = `translateY(${current}px)`
    if (progress) {
      progress(current)
    }

    if (current === targetPosition) {
      clearInterval(repeater)
      if (done) {
        done()
      }
    }
  }, 8)
}

interface InitPanelParam {
  element: HTMLElement
  animation: boolean
  draggable: boolean
  done?: (close: boolean) => void
  startOffset?: number
  step?: number
  afterInit?: () => void
  progress?: (pos: number) => void
  beforeClose?: () => void
}

let originHeight = 0

/**
 * 패널을 초기화 합니다.
 */
function initPanel({
  element,
  animation,
  draggable,
  done,
  startOffset = 0,
  step = 36,
  afterInit,
  progress,
  beforeClose,
}: InitPanelParam) {
  originHeight = Math.min(parseInt(getComputedStyle(element).height.replace('px', '')), window.innerHeight)
  element.style.height = `${originHeight}px`

  if (draggable) {
    const manager = new Hammer.Manager(element)
    manager.add(new Hammer.Pan())

    manager.on('panup pandown', (e: any) => {
      const top = Math.max(Math.min(originHeight - e.deltaY, originHeight), 0)
      element.style.transform = `translateY(-${top}px)`

      if (progress) {
        progress(-top)
      }
    })

    manager.on('panend', () => {
      const current = parseInt(element.style.transform.replace(/[A-Za-z()]/g, ''))
      const close = -current < originHeight / 2
      const targetPosition = close ? 0 : -originHeight

      if (close && beforeClose) {
        beforeClose()
      }

      movePanel(element, current, targetPosition, step, () => {
        if (done) {
          done(close)
        }
      })
    })
  }

  if (animation) {
    movePanel(element, startOffset, -originHeight, step, () => {
      element.style.height = ''

      if (afterInit) {
        afterInit()
      }
    })
  } else {
    element.style.transform = `translateY(${-originHeight}px)`
    element.style.height = ''
    if (afterInit) {
      afterInit()
    }
  }

  const hidePanel = () => {
    const current = parseInt(element.style.transform.replace(/[A-Za-z()]/g, ''))

    movePanel(element, current, 0, step, () => {
      if (done) {
        done(true)
      }
    })
  }

  return {
    hidePanel,
  }
}

export default defineComponent({
  name: 'SwipeablePanel',
  props: {
    hideHead: Boolean,
    draggable: Boolean,
    animation: {
      type: Boolean,
      default: true,
    },
    startOffset: {
      type: Number,
      default: 0,
    },
    step: {
      type: Number,
      default: 36,
    },
    baseOffset: {
      type: Number,
      default: 0,
    },
    overlay: {
      type: Boolean,
      default: false,
    },
    dark: {
      type: Boolean,
      default: false,
    },
    isBottomSpace: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['before-close', 'close', 'backclick'],
  setup(props, { emit }) {
    const inner = ref<HTMLElement>()
    let hidePanelPtr: () => void
    const closePanel = () => {
      if (hidePanelPtr) {
        emit('close', true)
        hidePanelPtr()
      }
    }

    const showBackground = ref(false)
    const backgroundTop = ref(0)

    const recalculate = ({ animation = true } = {}) => {
      nextTick(() => {
        if (inner.value) {
          const current = parseInt(inner.value.style.transform.replace(/[A-Za-z()]/g, ''))
          originHeight = Math.min(parseInt(getComputedStyle(inner.value).height.replace('px', '')), window.innerHeight)

          if (!animation || !props.animation) {
            inner.value.style.transform = `translateY(${-originHeight}px)`
            backgroundTop.value = -originHeight
          } else {
            movePanel(inner.value, current, -originHeight, 5, undefined, (pos: number) => {
              backgroundTop.value = pos
            })
          }
        }
      })
    }

    useHistory(HistoryQueryStringType.PANEL, closePanel)

    onMounted(async () => {
      const panel = inner.value as HTMLElement
      const { hidePanel } = initPanel({
        step: props.step,
        element: panel,
        animation: props.animation,
        draggable: props.draggable,
        done: (close) => {
          emit('close', close)
        },
        startOffset: props.startOffset,
        afterInit: () => {
          if (!showBackground.value) {
            showBackground.value = true
          }
        },
        progress: (pos: number) => {
          backgroundTop.value = pos
        },
        beforeClose: () => {
          showBackground.value = false
          emit('before-close')
        },
      })

      hidePanelPtr = hidePanel

      if (inner.value) {
        backgroundTop.value = -parseInt(getComputedStyle(inner.value).height.replace('px', ''))
      }
    })

    return {
      showBackground,
      inner,
      recalculate,
      close: closePanel,
      backgroundTop,
    }
  },
})
